<script setup>
//TabContentWrapper.vue
import { TabsContent } from 'dashboard/components-shadcn/new-york/ui/tabs';
import { ArrowLeft, ArrowRight, Eye, Sparkles } from 'lucide-vue-next';

const props = defineProps({
  tabId: {
    type: String,
    required: true
  },
  description: {
    type: String,
    required: true
  },
  showBackButton: {
    type: Boolean,
    default: true
  },
  backButtonLabel: {
    type: String,
    default: 'Voltar'
  },
  showDefaultAction: {
    type: Boolean,
    default: true
  },
  isLastTab: {
    type: Boolean,
    default: false
  },
  nextDisabled: {
    type: Boolean,
    default: false
  },
  nextButtonLabel: {
    type: String,
    default: 'Próximo'
  },
  submitButtonLabel: {
    type: String,
    default: 'Finalizar'
  },
  showPreviewButton: {
    type: Boolean,
    default: false
  },
  previewDisabled: {
    type: Boolean,
    default: false
  },
  previewButtonLabel: {
    type: String,
    default: 'Pré-visualizar Dados'
  }
});

const emit = defineEmits(['back', 'next', 'submit', 'preview']);
</script>

<template>
  <TabsContent :value="tabId" class="space-y-6">
    <div class="modern-tab-container">
      <!-- Decorative Background Elements -->
      <div class="background-decoration">
        <div class="decoration-blob decoration-blob-1"></div>
        <div class="decoration-blob decoration-blob-2"></div>
      </div>

      <!-- Header Section -->
      <div class="tab-header" v-if="description">
        <div class="header-content">
          <div class="accent-line"></div>
          <p class="description-text">{{ description }}</p>
        </div>
      </div>

      <!-- Main Content Area -->
      <div class="main-content-area">
        <slot name="content" />
      </div>

      <!-- Action Bar -->
      <div class="action-bar">
        <div class="action-bar-background"></div>
        
        <!-- Back Button -->
        <div class="back-section">
          <button
            v-if="showBackButton"
            type="button"
            @click="$emit('back')"
            class="modern-button back-button"
          >
            <div class="button-shine"></div>
            <ArrowLeft class="button-icon" />
            <span class="hidden sm:inline">{{ backButtonLabel }}</span>
            <span class="sm:hidden">Voltar</span>
          </button>
        </div>

        <!-- Center Actions Slot -->
        <div class="center-actions">
          <slot name="actions" />
        </div>

        <!-- Primary Actions -->
        <div class="primary-actions">
          <!-- Preview Button -->
          <button
            v-if="showPreviewButton"
            type="button"
            @click="$emit('preview')"
            :disabled="previewDisabled"
            class="modern-button preview-button"
          >
            <div class="button-shine"></div>
            <div class="preview-glow"></div>
            <Eye class="button-icon" />
            <span class="hidden sm:inline">{{ previewButtonLabel }}</span>
            <span class="sm:hidden">Preview</span>
          </button>

          <!-- Next/Submit Button -->
          <button
            type="button"
            @click="$emit(isLastTab ? 'submit' : 'next')"
            :disabled="nextDisabled"
            class="modern-button primary-button"
          >
            <div class="primary-gradient"></div>
            <div class="primary-glow"></div>
            <div class="button-content">
              <span v-if="isLastTab" class="button-text">
                {{ submitButtonLabel }}
                <Sparkles class="button-icon ml-2" />
              </span>
              <span v-else class="button-text">
                <span class="hidden sm:inline">{{ nextButtonLabel }}</span>
                <span class="sm:hidden">Próximo</span>
                <ArrowRight class="button-icon ml-2" />
              </span>
            </div>
          </button>
        </div>
      </div>
    </div>
  </TabsContent>
</template>



<style scoped>
/* Container Principal */
.modern-tab-container {
  @apply relative min-h-[520px] flex flex-col overflow-hidden rounded-[20px] border border-woot-400 shadow-lg;
  background: linear-gradient(
    135deg,
    rgb(var(--solid-1)) 0%,
    rgb(var(--solid-2)) 50%,
    rgb(var(--solid-1)) 100%
  );
  box-shadow:
    0 20px 40px rgb(var(--slate-4) / 0.1),
    0 1px 3px rgb(var(--slate-6) / 0.1);
}


/* Elementos Decorativos de Fundo */
.background-decoration {
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
}

.decoration-blob {
  position: absolute;
  border-radius: 50%;
  filter: blur(40px);
  opacity: 0.6;
  animation: float 8s ease-in-out infinite;
}

.decoration-blob-1 {
  width: 200px;
  height: 200px;
  background: radial-gradient(circle, rgb(var(--teal-5) / 0.3), transparent);
  top: -50px;
  right: -50px;
  animation-delay: 0s;
}

.decoration-blob-2 {
  width: 150px;
  height: 150px;
  background: radial-gradient(circle, rgb(var(--iris-5) / 0.25), transparent);
  bottom: -30px;
  left: -30px;
  animation-delay: 4s;
}

@keyframes float {
  0%, 100% { transform: translateY(0px) scale(1); }
  50% { transform: translateY(-20px) scale(1.1); }
}

/* Header */
.tab-header {
  position: relative;
  padding: 32px;
  border-bottom: 1px solid transparent;
  background: linear-gradient(90deg, transparent, rgb(var(--teal-6) / 0.3), rgb(var(--iris-6) / 0.3), transparent) bottom;
  background-size: 100% 1px;
  background-repeat: no-repeat;
}

.header-content {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.accent-line {
  width: 4px;
  height: 60px;
  border-radius: 2px;
  background: linear-gradient(180deg, rgb(var(--teal-9)), rgb(var(--iris-9)));
  animation: pulse 3s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 0.8; transform: scaleY(1); }
  50% { opacity: 1; transform: scaleY(1.1); }
}

.description-text {
  font-size: 16px;
  line-height: 1.6;
  font-weight: 500;
  color: rgb(var(--slate-11));
  background: linear-gradient(135deg, rgb(var(--slate-11)), rgb(var(--slate-9)));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  flex: 1;
}

/* Main Content */
.main-content-area {
  flex: 1;
  padding: 24px 32px;
  position: relative;
  z-index: 1;
}

/* Action Bar */
.action-bar {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 24px 32px;
  background: linear-gradient(135deg, rgb(var(--solid-2) / 0.9), rgb(var(--solid-3) / 0.8));
  border-top: 1px solid rgb(var(--teal-6) / 0.3);
  backdrop-filter: blur(10px);
}

.action-bar-background {
  position: absolute;
  inset: 0;
  background: 
    radial-gradient(circle at 0% 50%, rgb(var(--teal-4) / 0.1), transparent 50%),
    radial-gradient(circle at 100% 50%, rgb(var(--iris-4) / 0.1), transparent 50%);
  pointer-events: none;
}

.back-section {
  flex-shrink: 0;
}

.center-actions {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
}

.primary-actions {
  display: flex;
  gap: 12px;
  flex-shrink: 0;
}

/* Base Button Styles */
.modern-button {
  position: relative;
  height: 48px;
  padding: 0 24px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: none;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  min-width: 140px;
  overflow: hidden;
}

.modern-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none !important;
}

/* Button Shine Effect */
.button-shine {
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.3) 50%, transparent 70%);
  transform: translateX(-100%);
  transition: transform 0.6s;
  pointer-events: none;
}

.modern-button:hover .button-shine {
  transform: translateX(100%);
}

/* Back Button */
.back-button {
  background: linear-gradient(135deg, rgb(var(--solid-2)), rgb(var(--solid-3)));
  color: rgb(var(--slate-11));
  border: 1px solid rgb(var(--border-weak));
}

.back-button:hover {
  background: linear-gradient(135deg, rgb(var(--solid-3)), rgb(var(--slate-3)));
  border-color: rgb(var(--slate-6) / 0.6);
  transform: translateX(-2px) scale(1.02);
  box-shadow: 0 8px 25px rgb(var(--slate-4) / 0.2);
}

/* Preview Button */
.preview-button {
  background: linear-gradient(135deg, transparent, rgb(var(--iris-2) / 0.3));
  color: rgb(var(--slate-11));
  border: 2px solid rgb(var(--iris-6) / 0.5);
  position: relative;
}

.preview-glow {
  position: absolute;
  inset: -2px;
  background: linear-gradient(135deg, rgb(var(--iris-5) / 0.5), rgb(var(--blue-5) / 0.5));
  border-radius: 12px;
  opacity: 0;
  transition: opacity 0.3s;
  z-index: -1;
  filter: blur(8px);
}

.preview-button:hover {
  background: linear-gradient(135deg, rgb(var(--iris-2) / 0.5), rgb(var(--blue-2) / 0.5));
  border-color: rgb(var(--iris-7) / 0.8);
  color: rgb(var(--iris-11));
  transform: scale(1.05);
}

.preview-button:hover .preview-glow {
  opacity: 1;
}

/* Primary Button */
.primary-button {
  position: relative;
  color: white;
  font-weight: 700;
  border: none;
}

.primary-gradient {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgb(var(--teal-9)), rgb(var(--teal-10)), rgb(var(--teal-11)));
  border-radius: 12px;
  transition: all 0.3s;
}

.primary-glow {
  position: absolute;
  inset: -4px;
  background: linear-gradient(135deg, rgb(var(--teal-6) / 0.6), rgb(var(--teal-8) / 0.4));
  border-radius: 16px;
  opacity: 0;
  transition: opacity 0.3s;
  z-index: -1;
  filter: blur(12px);
}

.button-content {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.primary-button:hover {
  transform: translateY(-2px) scale(1.05);
}

.primary-button:hover .primary-gradient {
  background: linear-gradient(135deg, rgb(var(--teal-10)), rgb(var(--teal-11)), rgb(var(--teal-12)));
  animation: gradient-shift 2s ease-in-out infinite;
}

.primary-button:hover .primary-glow {
  opacity: 1;
}

@keyframes gradient-shift {
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
}

.primary-button:active {
  transform: translateY(0) scale(1.02);
}

/* Button Elements */
.button-icon {
  width: 16px;
  height: 16px;
  transition: transform 0.3s;
}

.button-text {
  display: flex;
  align-items: center;
  gap: 8px;
}

.back-button:hover .button-icon {
  transform: translateX(-2px);
}

.primary-button:hover .button-icon {
  transform: translateX(2px) scale(1.1);
}

.preview-button:hover .button-icon {
  transform: scale(1.1);
}

/* Responsive */
@media (max-width: 640px) {
  .modern-tab-container {
    min-height: 480px;
    border-radius: 16px;
  }
  
  .tab-header, .main-content-area, .action-bar {
    padding: 20px;
  }
  
  .action-bar {
    flex-direction: column;
    gap: 12px;
  }
  
  .back-section, .primary-actions {
    width: 100%;
  }
  
  .primary-actions {
    flex-direction: column;
  }
  
  .modern-button {
    min-width: 100%;
    height: 52px;
    font-size: 16px;
  }
  
  .description-text {
    font-size: 15px;
  }
}

/* Dark Mode */
@media (prefers-color-scheme: dark) {
  .modern-tab-container {
    background: linear-gradient(
      135deg,
      rgb(var(--solid-1) / 0.95) 0%,
      rgb(var(--solid-2) / 0.9) 50%,
      rgb(var(--solid-1) / 0.95) 100%
    );
    box-shadow: 
      0 20px 40px rgb(var(--slate-12) / 0.3),
      0 1px 3px rgb(var(--slate-12) / 0.2);
  }
  
  .description-text {
    color: rgb(var(--slate-9));
    background: linear-gradient(135deg, rgb(var(--slate-9)), rgb(var(--slate-8)));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  
  .action-bar {
    background: linear-gradient(135deg, rgb(var(--solid-2) / 0.8), rgb(var(--solid-3) / 0.7));
    border-top-color: rgb(var(--teal-7) / 0.4);
  }
  
  .back-button {
    color: rgb(var(--slate-9));
  }
  
  .preview-button {
    color: rgb(var(--slate-9));
  }
  
  .preview-button:hover {
    color: rgb(var(--iris-9));
  }
}
</style>